import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import BookingWidget from "../components/BookingWidget";

function PriceRow({ label, value }) {
  if (value == null) return null;
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-600">{label}</span>
      <span className="font-semibold">
        {Number(value).toLocaleString("no-NO")} kr
      </span>
    </div>
  );
}

function PurchaseBox({ product, onAdd }) {
  const [qty, setQty] = useState(1);
  const stock = product?.stock_qty;
  const outOfStock = Number.isInteger(stock) && stock <= 0;

  return (
    <div className="w-full rounded-xl border bg-white/60 p-4">
      <div className="flex items-baseline justify-between">
        <h3 className="text-lg font-semibold">Kjøp</h3>
        <div className="text-sm text-gray-600">
          {Number.isInteger(stock)
            ? stock > 0
              ? `På lager: ${stock}`
              : <span className="text-red-600">Tomt på lager</span>
            : "På lager"}
        </div>
      </div>
      <div className="mt-2 text-2xl font-bold">
        {Number(product.price_sale).toLocaleString("no-NO")} kr
      </div>

      <div className="mt-4 flex items-center gap-3">
        <label className="text-sm text-gray-700" htmlFor="qty">Antall</label>
        <input
          id="qty"
          type="number"
          min={1}
          step={1}
          value={qty}
          onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))}
          className="w-24 rounded border px-3 py-2"
        />
        <button
          className="ml-auto rounded bg-[#000099] px-5 py-2 font-semibold text-white hover:bg-blue-900 disabled:cursor-not-allowed disabled:bg-blue-300"
          onClick={() => onAdd(qty)}
          disabled={outOfStock}
        >
          Legg i handlekurv
        </button>
      </div>
    </div>
  );
}

export default function ProductPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/products/${id}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setProduct)
      .catch(() => setError("Kunne ikke hente produkt"));
  }, [id]);

  const fetchBookings = async (productId) => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/products/${productId}/bookings`
    );
    if (!res.ok) return [];
    return await res.json();
  };

  const checkAvailability = async ({ productId, from, to, quantity = 1 }) => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/availability?productId=${encodeURIComponent(productId)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&quantity=${encodeURIComponent(quantity)}`
    );

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(data.error || "Kunne ikke sjekke tilgjengelighet");
    }

    return data;
  };

    const handleAddToCartRental = (bookingData) => {
      addToCart(product, {
        ...bookingData,
        quantity: bookingData.quantity || 1,
      });
      setSuccess(true);
    };

  const handleAddToCartPurchase = (qty) => {
    addToCart(product, {
      quantity: qty,
      price: product.price_sale,
    });
    setSuccess(true);
  };

  if (error) return <p className="mt-6 text-center text-red-500">{error}</p>;
  if (!product) return <p className="mt-6 text-center">Laster…</p>;

  const isPurchasable = !!product.is_for_sale && product.price_sale != null;
  const hasRentalPrices =
    product.price_day != null ||
    product.price_short_4h != null ||
    product.price_weekend != null;
  const showBooking = !isPurchasable && hasRentalPrices;

  return (
    <div className="w-full flex flex-col items-center">
      <div className="mx-auto mt-4 mb-6 w-[90%] max-w-5xl">
        {product.ProductGroupId ? (
          <Link
            to={`/group/${product.ProductGroupId}`}
            className="mb-4 inline-block text-blue-600 hover:underline"
          >
            ← Tilbake til kategorier
          </Link>
        ) : (
          <Link to="/" className="mb-4 inline-block text-blue-600 hover:underline">
            ← Tilbake
          </Link>
        )}
      </div>

      <div className="w-[100%] max-w-7xl flex flex-col gap-10 md:flex-row">
        <div className="min-h-[450px] w-full rounded-xl bg-blue-50 p-8 md:w-1/2">
          <h1 className="mb-4 text-3xl font-bold text-[#000099]">{product.name}</h1>

          {product.image_url && (
            <img
              src={product.image_url}
              alt={product.name}
              className="mb-4 h-64 w-full rounded object-cover shadow"
            />
          )}

          <p className="mb-5">{product.description}</p>

          <div className="rounded-xl border bg-white/60 p-4">
            <div className="mb-2 font-semibold text-[#000099]">Priser</div>

            {isPurchasable ? (
              <div>
                <div className="text-sm text-gray-600">Per stk</div>
                <div className="mt-1 text-2xl font-bold">
                  {Number(product.price_sale).toLocaleString("no-NO")} kr
                </div>
              </div>
            ) : hasRentalPrices ? (
              <>
                <PriceRow label="&lt; 4 timer" value={product.price_short_4h} />
                <PriceRow label="Døgn" value={product.price_day} />
                <PriceRow label="Weekend (fre 16 → søn 22)" value={product.price_weekend} />
              </>
            ) : (
              <div className="text-sm text-gray-600">Ingen pris oppgitt</div>
            )}
          </div>
        </div>

        <div className="w-full rounded-xl bg-blue-50 md:w-1/2">
          <div className="min-h-[450px] w-full rounded-2xl p-8">
            {!success && (
              <>
                {showBooking && (
                  <BookingWidget
                    product={product}
                    fetchBookings={fetchBookings}
                    checkAvailability={checkAvailability}
                    onAddToCart={handleAddToCartRental}
                  />
                )}

                {isPurchasable && (
                  <PurchaseBox product={product} onAdd={handleAddToCartPurchase} />
                )}

                {!showBooking && !isPurchasable && (
                  <div className="rounded-xl border bg-white/60 p-4 text-center text-gray-600">
                    Kontakt oss for pris/tilgjengelighet.
                  </div>
                )}
              </>
            )}

            {success && (
              <div className="flex w-full flex-col items-center gap-3">
                <div className="mt-2 animate-pulse text-center font-semibold text-green-600">
                  ✅ Lagt til i handlekurven!
                </div>
                <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                  <button
                    onClick={() => navigate("/")}
                    className="rounded bg-[#000099] px-6 py-2 font-semibold text-white transition hover:bg-blue-900"
                  >
                    Handle videre
                  </button>
                  <button
                    onClick={() => navigate("/cart")}
                    className="rounded bg-green-600 px-6 py-2 font-semibold text-white transition hover:bg-green-700"
                  >
                    Til kasse
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}